import { createElement, type HTMLAttributes, type ReactElement, type Ref } from 'react';

import { classNames } from '@m1work/shared-utils';

import type { LayoutElement } from '../types/layoutElement';
import type { Responsive } from '../types/responsive';
import type { Space } from '../types/space';
import { getResponsiveClassNames } from '../utils/getResponsiveClassNames';

import styles from './Flex.module.scss';

export type FlexDirection = 'row' | 'column' | 'row-reverse' | 'column-reverse';
export type FlexAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
export type FlexJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
export type FlexWrap = 'nowrap' | 'wrap' | 'wrap-reverse';

export interface FlexProps extends HTMLAttributes<HTMLElement> {
    /** HTML element to render. @default 'div' */
    as?: LayoutElement;
    /** Main axis: `row` lays children out horizontally, `column` vertically. @default 'row' */
    direction?: Responsive<FlexDirection>;
    /** Alignment across the main axis (`align-items`). @default 'stretch' */
    align?: Responsive<FlexAlign>;
    /** Distribution along the main axis (`justify-content`); `between` = space-between. @default 'start' */
    justify?: Responsive<FlexJustify>;
    /** Whether children wrap onto new lines. @default 'nowrap' */
    wrap?: Responsive<FlexWrap>;
    /** Space between children, a step of the spacing scale (4 = 16px). The only way to space siblings. */
    gap?: Responsive<Space>;
    /** Render as `inline-flex` instead of `flex`. @default false */
    isInline?: boolean;
    /** Take the free space inside a parent flex container (`flex-grow: 1`). @default false */
    shouldGrow?: boolean;
    ref?: Ref<HTMLElement>;
}

/**
 * Lays out its children in a row or a column, with spacing from the spacing scale.
 * Layout only: no padding, background or borders. Use a dedicated component for a surface (card, panel),
 * and `Grid` for two-dimensional layouts.
 *
 * @example
 * <Flex direction={{ base: 'column', md: 'row' }} align="center" justify="between" gap={4}>
 *     <Logo />
 *     <Navigation />
 * </Flex>
 */
export function Flex({
    as = 'div',
    direction,
    align,
    justify,
    wrap,
    gap,
    isInline = false,
    shouldGrow = false,
    className,
    ...props
}: FlexProps): ReactElement {
    // createElement instead of JSX: its overload for HTML tags is generic over the element type, so one
    // `ref: Ref<HTMLElement>` fits every tag allowed in `as`.
    return createElement(as, {
        className: classNames(
            isInline ? styles.inline : styles.flex,
            shouldGrow && styles.grow,
            ...getResponsiveClassNames(styles, 'dir', direction),
            ...getResponsiveClassNames(styles, 'align', align),
            ...getResponsiveClassNames(styles, 'justify', justify),
            ...getResponsiveClassNames(styles, 'wrap', wrap),
            ...getResponsiveClassNames(styles, 'gap', gap),
            className,
        ),
        ...props,
    });
}
